import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CompanyServices } from "$services";
import { Company } from "$types/Company";
import useOrder from "$hooks/useOrder";
import useAuth from "$hooks/useAuth";
import { unsensitiveIsSameString } from "$utils/utils";
import useCompany from "$hooks/useCompany";
import useCompanyForm from "$hooks/useCompanyForm";
import { appState } from "$reducers";
import { queryClient } from "$utils/queryClient";
import { useSocket } from "$utils/socket";
import { CompanyForm } from "$containers/Entreprises/EntrepriseForm";
import { CompanyType } from "$types";

type Props = {
  defaultValues?: Partial<Company>;
};

export default function useCompanyFormDetails({ defaultValues }: Props) {
  const socket = useSocket();
  const { token } = useAuth();
  const { classes: sclasses } = useOrder();
  const { isAgency, isClient, isContributor } = useCompany();
  const { filter: sidebarCompanyFilter } = useSelector(appState);

  const initialName = defaultValues?.name;
  const initialSiret = defaultValues?.siret;

  const postSubmitCarrier = (carrierId: string) =>
    methods.setValue("agencyOf", carrierId);

  const {
    isOpened,
    togglePopin,
    handleSubmit: handleSubmitCarrier,
    handleSiretData,
  } = useCompanyForm(postSubmitCarrier);

  const [companies, setCompanies] = useState([]);
  const [isAutofillError, setIsAutofillError] = useState(false);
  const [matchingCompanies, setMatchingCompanies] = useState([]);
  const [isCompanyFilledUsed, setIsCompanyFilledUsed] = useState(false);
  const [isAutofillAlreadyUsed, setIsAutofillAlreadyUsed] = useState(false);

  const formDefaultValues = useMemo(() => {
    const baseDefaultValues = {
      _id: null,
      adress: "",
      supplierOf: null,
      agencyOf: null,
      activities: [],
      companyTypes: [],
      complementAdress: "",
      contributorOf: [],
      contributingCompanies: [],
      city: "",
      country: "France",
      email: "",
      name: "",
      notes: "",
      postalCode: "",
      siret: "",
      sector: null,
      technicalNotes: "",
    };

    if (!defaultValues) {
      return baseDefaultValues;
    }

    return {
      ...baseDefaultValues,
      ...defaultValues,
      supplierOf: defaultValues.supplierOf?.map((c) => c._id),
      agencyOf: defaultValues.agencyOf?._id || defaultValues.agencyOf,
      activities: defaultValues.activities || [],
      companyTypes:
        defaultValues.companyTypes?.length > 0
          ? defaultValues.companyTypes
          : [CompanyType.CARRIER],
      contributorOf: defaultValues?.contributorOf?.map((c) => c._id),
      country: defaultValues.country || "France",
      contributingCompanies: isClient(defaultValues?.companyTypes)
        ? companies?.reduce(
            (res: string[], c: Company) =>
              c?.contributorOf?.includes(defaultValues?._id)
                ? [...res, c._id]
                : res,
            []
          )
        : [],
    };
  }, [companies, defaultValues, isClient]);

  const methods = useForm<CompanyForm>({
    mode: "onChange",
    defaultValues: formDefaultValues,
  });

  const fetchCompaniesFilter = useMemo(() => {
    return { page: 1, rowsPerPage: 50000 };
  }, []);

  const fecthCompanies = useCallback(async () => {
    let companies;
    companies = await queryClient.getQueryData([
      "getCompanies",
      fetchCompaniesFilter,
    ]);
    if (!companies) {
      companies = await queryClient.fetchQuery(
        ["getCompanies", fetchCompaniesFilter],
        () => CompanyServices.getCompanies(fetchCompaniesFilter),
        { staleTime: 60 * 1000 }
      );
      companies = companies.data;
      await queryClient.setQueryData(
        ["getCompanies", fetchCompaniesFilter],
        companies
      );
    }
    setCompanies(companies);
  }, [setCompanies, fetchCompaniesFilter]);

  const relatedCarrierId = methods.watch(
    "agencyOf",
    formDefaultValues.agencyOf
  );

  const siretValue = methods.watch("siret", formDefaultValues.siret);
  const country = methods.watch("country", formDefaultValues.country);

  const companyTypes = methods.watch(
    "companyTypes",
    formDefaultValues.companyTypes
  );

  const validateName = (newName: string) => {
    // Insensitive to case and accents comparison + initial name is valid
    const doesACompanyExists = (selectedCompanies: any[]) =>
      selectedCompanies.find((company) =>
        unsensitiveIsSameString(company.name, newName)
      ) !== undefined;

    const isNameUnchanged = initialName
      ? unsensitiveIsSameString(initialName, newName)
      : false;

    if (isAgency(companyTypes)) {
      if (relatedCarrierId) {
        return (
          !doesACompanyExists(
            companies.filter((c) => c.agencyOf === relatedCarrierId)
          ) || isNameUnchanged
        );
      }
      // Temporarily, when an agency is not attached to a carrier, it can have any name
      return true;
    }
    return !doesACompanyExists(companies) || isNameUnchanged;
  };

  const validateSiret = (newSiret: string) => {
    if (!newSiret) return true;
    // Initial siret is valid
    const doesACompanyExists = (selectedCompanies: any[]) =>
      selectedCompanies.find((company) =>
        unsensitiveIsSameString(company.siret || "", newSiret)
      ) !== undefined;

    const isSiretUnchanged = initialSiret
      ? unsensitiveIsSameString(initialSiret, newSiret)
      : false;

    if (isAgency(companyTypes)) {
      if (relatedCarrierId) {
        return (
          !doesACompanyExists(
            companies.filter((c) => c.agencyOf === relatedCarrierId)
          ) || isSiretUnchanged
        );
      }
      // Temporarily, when an agency is not attached to a carrier, it can have any SIRET
      return true;
    }
    return !doesACompanyExists(companies) || isSiretUnchanged;
  };

  const handleAutoFillClicked = async () => {
    const result = await handleSiretData(siretValue);
    setIsAutofillAlreadyUsed(true);
    if (!result || result.error) {
      setIsAutofillError(true);
      return;
    }

    const { etablissement } = result;
    const name = etablissement?.uniteLegale?.denominationUniteLegale;
    const adresseEtablissement = etablissement?.adresseEtablissement;
    const {
      codePostalEtablissement: postalCode,
      libelleCommuneEtablissement: city,
      libelleVoieEtablissement: streetName,
      numeroVoieEtablissement: houseNumber,
      typeVoieEtablissement: streetType,
    } = adresseEtablissement || {};

    if (name) {
      methods.setValue("name", name);
    }
    if (houseNumber && streetType && streetName) {
      methods.setValue("adress", `${houseNumber} ${streetType} ${streetName}`);
    }
    if (postalCode) {
      methods.setValue("postalCode", postalCode);
    }
    if (city) {
      methods.setValue("city", city);
    }
  };

  const fuzzySearchOnCompaniesName = useCallback(
    async (input: string) => {
      if (input.length <= 2) return;

      try {
        const warehouses = await CompanyServices.getCompaniesByName(
          token,
          input
        );
        setMatchingCompanies(warehouses || []);
      } catch (error) {
        console.error("Error searching companies:", error);
        setMatchingCompanies([]);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!methods.getValues("contributorOf")) {
      if (isContributor(companyTypes) && sidebarCompanyFilter) {
        methods.setValue("contributorOf", [sidebarCompanyFilter?._id]);
      } else {
        methods.setValue("contributorOf", undefined);
      }
    }
  }, [methods, isContributor, sidebarCompanyFilter, companyTypes]);

  useEffect(() => {
    if (isAutofillAlreadyUsed) {
      setIsAutofillAlreadyUsed(false);
    }

    if (isAutofillError) {
      setIsAutofillError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siretValue]);

  useEffect(() => {
    // @todo: completely rework this after socket refacto
    if (defaultValues?.agencyOf) {
      queryClient.removeQueries(["getCompanies", fetchCompaniesFilter]);
    }
    fecthCompanies();
  }, [defaultValues?.agencyOf, socket, fetchCompaniesFilter, fecthCompanies]);

  useEffect(() => {
    methods.reset({ ...methods.getValues(), ...formDefaultValues });
  }, [formDefaultValues, methods]);

  return {
    sclasses,
    sidebarCompanyFilter,
    postSubmitCarrier,
    isOpened,
    togglePopin,
    handleSubmitCarrier,
    handleSiretData,
    isAutofillAlreadyUsed,
    setIsAutofillAlreadyUsed,
    isAutofillError,
    setIsAutofillError,
    isCompanyFilledUsed,
    setIsCompanyFilledUsed,
    matchingCompanies,
    setMatchingCompanies,
    companies,
    setCompanies,
    socket,
    fetchCompaniesFilter,
    fecthCompanies,
    formDefaultValues,
    companyTypes,
    methods,
    ...methods,
    initialName,
    initialSiret,
    relatedCarrierId,
    country,
    siretValue,
    validateName,
    validateSiret,
    handleAutoFillClicked,
    fuzzySearchOnCompaniesName,
  };
}
